import React, { useEffect, useEffectEvent } from "react";

import { IgButton, IgLoading, IgTypography } from "@intelligems/ig-theme";
import { EmbeddedPreviewError } from "@src/features/experienceBuilder/pageState/EmbeddedPreviewError";
import { useManualDeploy } from "@src/api/plugin-configs/manual-deploy";
import {
  ExperienceBuilderAction as Action,
  ExperienceBuilderSelector as Selector,
} from "@src/store/slices/experienceBuilder";
import { useDispatch, useSelector } from "@src/store";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material";
import { IgCoreTextColors } from "@intelligems/ig-design-tokens";

export const PluginVersionMismatchError = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDeploying = useSelector(Selector.isDeploying);
  const hasAutoDeployed = useSelector(Selector.hasAutoDeployedPluginMismatch);
  const manualDeploy = useManualDeploy();

  const deploy = () => {
    dispatch(Action.setIsDeploying(true));
    manualDeploy.mutateAsync().catch(() => {
      dispatch(Action.setIsDeploying(false));
    });
  };

  // Auto-deploy on first mount of the mismatch error this session. Latched in
  // redux so a deploy that fails to clear the mismatch (or any remount) won't
  // re-trigger; the user can still retry via the manual Deploy button.
  const autoDeploy = useEffectEvent(() => {
    if (hasAutoDeployed) return;
    dispatch(Action.setHasAutoDeployedPluginMismatch(true));
    deploy();
  });
  useEffect(() => {
    autoDeploy();
  }, []);

  const description = isDeploying
    ? "Updating your bundle, please wait..."
    : "The Intelligems bundle is out of date.";

  if (isDeploying) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        height="100%"
        gap={4}
        bgcolor={theme.igBackgroundColors.baseLightHigh}
      >
        <IgLoading />
        <IgTypography variant="body1" color={IgCoreTextColors.placeholder}>
          Updating your preview...
        </IgTypography>
      </Stack>
    );
  } else {
    return (
      <EmbeddedPreviewError
        title={"Bundle Version Mismatch"}
        description={description}
        buttons={
          <IgButton
            variant="contained"
            color={"warning"}
            onClick={deploy}
            sx={{ flex: 1 }}
          >
            Deploy
          </IgButton>
        }
      />
    );
  }
};
